import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — el contrato de Reconocimientos.
 *
 * ## Los doce endpoints NO cambian
 *
 * Son los mismos que pedía `page-kudos.js`, con los mismos parámetros. Lo que
 * cambia es **cuándo** se piden dos de ellos, y eso no es un cambio de
 * contrato:
 *
 *   · `/users/areas` — llenaba el desplegable de áreas del ranking al montar la
 *     pantalla. Ahora se pide igual al montar, porque el desplegable se ve
 *     desde el primer momento y un filtro vacío que se llena solo un segundo
 *     después es peor que esperar.
 *
 *   · `/users/directory` — el buscador de personas del compositor. Se pedía
 *     desde un `debounce` escrito a mano; ahora es una `useQuery` con la
 *     búsqueda en la clave, así que **repetir una búsqueda ya hecha no vuelve a
 *     preguntar**. Escribir «ana», borrar y volver a escribir «ana» costaba dos
 *     peticiones y ahora cuesta una.
 *
 * ## Los tipos están escritos a mano
 *
 * Deuda declarada, la misma que en Novedades y Perfil: el backend declara un
 * solo `response_model` en 27 routers, así que `/openapi.json` no describe
 * ninguna respuesta y no hay nada que generar. Copiados leyendo
 * `app/routers/content.py`. En cuanto los routers declaren su modelo, esto se
 * sustituye por lo generado y el compilador empieza a avisar cuando el backend
 * cambia de forma. Mientras tanto es una promesa, no una garantía.
 *
 * ## Lo que esta capa arregla por construcción
 *
 * `page-kudos.js` interpolaba tres nombres de persona sin escapar —el del
 * ranking y los dos de «X reconoció a Y»— y, peor, construía el manejador del
 * buscador metiendo el nombre **dentro de una cadena JavaScript dentro de un
 * atributo HTML**. Un apóstrofo cerraba la cadena y la fila dejaba de
 * responder: a quien se llamara `O'Brien` no se le podía dar un reconocimiento.
 * Aquí todo eso son nodos de texto y manejadores de React. No hay ni un
 * `dangerouslySetInnerHTML` en esta feature.
 */

/* ── Tipos ─────────────────────────────────────────────────────────────── */

/** Un valor corporativo. `key` es lo que viaja al crear un reconocimiento. */
export interface Valor {
  id?: number;
  key: string;
  icon: string;
  name: string;
  color?: string | null;
  points?: number | null;
  description?: string | null;
}

export interface Reconocimiento {
  id?: number;
  badge: string;
  message?: string | null;
  sender_name?: string | null;
  receiver_name?: string | null;
  to_company?: boolean;
  created_at: string;
}

export interface FilaDeRanking {
  full_name?: string | null;
  total_kudos: number;
}

export interface ValorContado {
  name: string;
  icon: string;
  color?: string | null;
  count: number;
}

export interface Campana {
  id: number;
  title: string;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  start_at?: string | null;
  end_at?: string | null;
  running?: boolean;
}

export interface PersonaDelDirectorio {
  id: number;
  full_name: string;
  position?: string | null;
}

export type Periodo = 'week' | 'month' | 'all';

export interface Area {
  id: number;
  name: string;
}

/* ── Claves de caché ───────────────────────────────────────────────────── */

export const claves = {
  valores: ['kudos', 'valores'] as const,
  muro: ['kudos', 'muro'] as const,
  ranking: (periodo: Periodo, areaId: string) => ['kudos', 'ranking', periodo, areaId] as const,
  porValor: (periodo: Periodo) => ['kudos', 'por-valor', periodo] as const,
  campanas: (todas: boolean) => ['kudos', 'campanas', todas] as const,
  areas: ['usuarios', 'areas'] as const,
};

/* ── Lecturas ──────────────────────────────────────────────────────────── */

/** Los valores corporativos con los que se reconoce.
 *
 *  Sin respaldo escrito a mano, a diferencia del vanilla: allí había ocho
 *  valores copiados en el archivo «por si la API no responde», y eso significa
 *  que un fallo del servidor se veía como una lista de valores plausible pero
 *  falsa — se podía elegir uno que no existe y el envío lo rechazaba después.
 *  Aquí, si la lista falla, se dice que falló. */
export function useValores(habilitada = true) {
  return useQuery({
    enabled: habilitada,
    queryKey: claves.valores,
    queryFn: () => api.get<Valor[]>('/kudos/badges'),
    staleTime: 5 * 60_000,
  });
}

export function useMuro() {
  return useQuery({
    queryKey: claves.muro,
    queryFn: () => api.get<Reconocimiento[]>('/kudos', { limit: 20 }),
  });
}

export function useRanking(periodo: Periodo, areaId: string) {
  return useQuery({
    queryKey: claves.ranking(periodo, areaId),
    queryFn: () =>
      api.get<FilaDeRanking[]>('/kudos/leaderboard', {
        period: periodo,
        ...(areaId ? { area_id: areaId } : {}),
      }),
  });
}

export function usePorValor(periodo: Periodo) {
  return useQuery({
    queryKey: claves.porValor(periodo),
    queryFn: () => api.get<{ by_value?: ValorContado[] }>('/kudos/stats', { period: periodo }),
  });
}

/** `todas` distingue el banner de la pantalla (solo las vigentes) de la lista
 *  del panel de gestión (también las terminadas, para poder borrarlas).
 *
 *  `habilitada` existe por un defecto que se vio en el navegador al migrar: los
 *  paneles de gestión se montan siempre —con `abierto` en falso— para que el
 *  diálogo pueda animar su entrada, y sin esto su consulta se disparaba igual.
 *  La pantalla hacía **siete** peticiones donde el vanilla hacía seis, y la
 *  séptima era una lista que nadie iba a mirar. */
export function useCampanas(todas = false, habilitada = true) {
  return useQuery({
    queryKey: claves.campanas(todas),
    queryFn: () =>
      api.get<Campana[]>('/kudos/campaigns', todas ? { all: 'true' } : undefined),
    enabled: habilitada,
  });
}

export function useAreas() {
  return useQuery({
    queryKey: claves.areas,
    queryFn: () => api.get<Area[]>('/users/areas'),
    staleTime: 10 * 60_000,
  });
}


/* ── Escrituras ────────────────────────────────────────────────────────── */

export interface ReconocimientoNuevo {
  receiver_id: number | null;
  to_company: boolean;
  badge: string;
  message: string | null;
}

/** Publicar un reconocimiento.
 *
 *  Invalida las tres vistas que dependen de él —el muro, el ranking y el
 *  recuento por valor—, y no solo el muro. En el vanilla eran tres llamadas
 *  escritas a mano una detrás de otra (`loadKudos(); loadByValue();
 *  loadLeaderboard();`), así que añadir una cuarta vista significaba acordarse
 *  de añadir la cuarta línea aquí. */
export function usePublicarReconocimiento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nuevo: ReconocimientoNuevo) => api.post<Reconocimiento>('/kudos', nuevo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kudos'] });
    },
  });
}

export interface ValorNuevo {
  name: string;
  icon: string;
  color: string;
  points: number;
}

export function useCrearValor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nuevo: ValorNuevo) => api.post<Valor>('/kudos/badges', nuevo),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.valores }),
  });
}

export function useBorrarValor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<void>(`/kudos/badges/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.valores }),
  });
}

export interface CampanaNueva {
  title: string;
  description: string | null;
  start_at: string | null;
  end_at: string | null;
  icon: string;
}

export function useCrearCampana() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nueva: CampanaNueva) => api.post<Campana>('/kudos/campaigns', nueva),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kudos', 'campanas'] }),
  });
}

export function useBorrarCampana() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<void>(`/kudos/campaigns/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kudos', 'campanas'] }),
  });
}
